import { useVerify } from '@entities/Authentication/model.ts';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCaptchaModal } from '@pages/personal/client/editor-page/Modals/CaptchaVerificationModal/model.ts';
import { CodeModal } from '@pages/personal/client/editor-page/Modals/CodeModal/index.tsx';
import { getTitle } from '@pages/personal/client/editor-page/Modals/helpers.ts';
import { useOpenPhoneVerify } from '@pages/personal/client/editor-page/Modals/VerifyPhone/model.ts';
import { useEditorClientSharedState } from '@pages/personal/client/editor-page/model.ts';
import { useSupportPageRedirect } from '@pages/support/model.ts';
import { usePlatformSettings, useUser } from '@shared/hooks/index.ts';
import type { RegistrationMethod } from '@shared/types/common';

const getModalContent = ({
  phoneNumber,
  step,
  first_method,
}: {
  step: number;
  phoneNumber?: string;
  first_method: string;
}) => {
  const isSmsFirst = first_method === 'SMS';
  const textOnSms = 'сообщение';
  const textOnCall = 'звонок';
  const buttonTextOnSms = `Отправить ${textOnSms}`;
  const buttonTextOnCall = `Позвонить повторно`;
  let callOrSms = isSmsFirst ? textOnSms : textOnCall;
  // после двух попыток первого метода отправки пробуем другой
  step === 3 && (callOrSms = isSmsFirst ? textOnCall : textOnSms);
  let repeatButtonText = isSmsFirst ? buttonTextOnSms : buttonTextOnCall;
  step === 2 && (repeatButtonText = isSmsFirst ? buttonTextOnCall : buttonTextOnSms);
  step === 3 && (repeatButtonText = 'Техническая поддержка');
  const phoneToDisplay = phoneNumber?.includes('+') ? phoneNumber : `+${phoneNumber}`;
  return {
    text: `На указанный вами номер ${phoneToDisplay} поступит ${callOrSms}. Введите последние 4 цифры`,
    repeatButtonText,
  };
};

export const VerificationPhoneModal = () => {
  const navigate = useNavigate();
  const { redirectToSupportPage } = useSupportPageRedirect();
  const { state, closePhoneVerificationModal } = useOpenPhoneVerify();
  const [isTemporaryClosed, setIsTemporaryClosed] = useState(false);
  const { updateEditorClientSharedState } = useEditorClientSharedState();
  const { user } = useUser();
  const { first_method } = usePlatformSettings();
  const { onCheckPhoneVerification } = useVerify();
  const { openCaptchaModal } = useCaptchaModal();

  const [passCode, setPassCode] = useState('');
  const [showTimer, setShowTimer] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [step, setStep] = useState(1);

  const onModalClose = useCallback(
    (props: { isSuccess: boolean; value?: string; passCode?: string }) => {
      closePhoneVerificationModal(props);
      setStep(1);
      setPassCode('');
      setErrorMessage('');
    },
    [closePhoneVerificationModal, setPassCode, setErrorMessage]
  );

  const onRepeatButtonClick = useCallback(async () => {
    if (step < 3) {
      let callOrSms = first_method === 'SMS' ? 'SMS' : 'CALL';
      // после двух попыток первого метода отправки пробуем другой
      step === 2 && (callOrSms = first_method === 'SMS' ? 'CALL' : 'SMS');
      setIsTemporaryClosed(true);
      openCaptchaModal({
        verification_type: state.verification_type,
        type: callOrSms as RegistrationMethod,
        onSuccess: () => {
          setIsTemporaryClosed(false);
          setShowTimer(true);
          setStep((step) => step + 1);
        },
      });
    } else {
      redirectToSupportPage();
      onModalClose({ isSuccess: false });
    }
  }, [navigate, first_method, openCaptchaModal, step, setStep, state, setShowTimer, onModalClose]);

  const onReadyButtonClick = useCallback(async () => {
    await onCheckPhoneVerification({
      verification_type: state.verification_type,
      passcode: passCode,
      phonenumber: state?.value,
      onError: (errMessage) => setErrorMessage(errMessage),
      onSuccess: () => {
        onModalClose({ isSuccess: true, value: state?.value, passCode });
        updateEditorClientSharedState({ phonenumber: state?.value, phonenumber_code: passCode });
      },
    });
  }, [state, passCode, onCheckPhoneVerification, updateEditorClientSharedState, onModalClose]);

  const { text, repeatButtonText } = getModalContent({
    step,
    first_method: first_method as string,
    phoneNumber: state?.value || user?.phonenumber,
  });

  return (
    <CodeModal
      isOpen={isTemporaryClosed ? false : state?.isModalOpen}
      passCode={passCode}
      setPassCode={setPassCode}
      text={text}
      onReadyButtonClick={onReadyButtonClick}
      title={getTitle(state?.verification_type)}
      showTimer={showTimer}
      cleanErrorMessage={() => setErrorMessage('')}
      errorMessage={errorMessage}
      onRepeatButtonClick={onRepeatButtonClick}
      repeatButtonText={repeatButtonText}
      onModalClose={() => onModalClose({ isSuccess: false })}
      setShowTimerFalse={() => setShowTimer(false)}
    />
  );
};
